import { Box, CircularProgress, alpha } from "@mui/material";
import Grid from "@mui/material/Grid2";
import dayjs from "dayjs";
import {
  MRT_ColumnDef,
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useTableSettings } from "@/hooks/use-table-settings";
import { colorPool } from "@/module/service-configs-module/statistics-visualization-module/first-svetofor/util";

import { AggregatedCell } from "./aggregated-cell";
import { useQueryHandHistoryAggregated } from "./hooks/use-query-hand-history-aggregated";
import {
  MttAggregatedValues,
  MttAnalyticsRow,
  SUMMARY_MARKER_ROW,
  SUMMARY_ROW_ID,
  calculateAggregatedValues,
  formatNumber,
  formatPercent,
} from "./utils";

const FILTERABLE_COLUMNS = [
  "equals",
  "notEquals",
  "between",
  "contains",
  "startsWith",
  "endsWith",
];

const FILTERABLE_COLUMN_IDS = [
  "pid",
  "room",
  "profile",
  "start_time_ms",
  "buyin_usd",
  "tournament",
  "date",
  "game_type",
];

export const MttAnalyticsPage = () => {
  const { t } = useTranslation();
  const { defaultProps, state, onPaginationChange } = useTableSettings<MttAnalyticsRow>(
    "mttAnalytics",
    {
      pageSize: 100,
    }
  );

  const columnFilterFns = useMemo(() => {
    return FILTERABLE_COLUMN_IDS.reduce<Record<string, string>>((acc, columnId) => {
      acc[columnId] = state.columnFilterFns[columnId] || "equals";
      return acc;
    }, {});
  }, [state.columnFilterFns]);

  useEffect(() => {
    onPaginationChange({ pageIndex: 0, pageSize: state.pagination.pageSize });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.columnFilters, state.grouping]);

  const { data: response, isLoading } = useQueryHandHistoryAggregated({
    grouping: state.grouping,
    pageNumber: state.pagination.pageIndex,
    pageSize: state.pagination.pageSize,
    columnFilters: state.columnFilters,
    columnFilterFns,
  });

  const rows = useMemo(() => response?.data.rows ?? [], [response?.data.rows]);

  const aggregatedValues = useMemo<MttAggregatedValues>(
    () => calculateAggregatedValues(rows),
    [rows]
  );

  const tableData = useMemo<MttAnalyticsRow[]>(() => {
    if (rows.length === 0) {
      return [];
    }

    const pageOffset = state.pagination.pageIndex * state.pagination.pageSize;
    const dataRows = rows.map((row, index) => ({
      ...row,
      _rowNumber: pageOffset + index + 1,
    }));

    return [SUMMARY_MARKER_ROW, ...dataRows];
  }, [rows, state.pagination.pageIndex, state.pagination.pageSize]);

  const columns = useMemo<MRT_ColumnDef<MttAnalyticsRow>[]>(
    () => [
      {
        id: "pid",
        header: t("pid"),
        accessorKey: "pid",
        enableGrouping: true,
        columnFilterModeOptions: FILTERABLE_COLUMNS,
        Cell: ({ row, cell }) =>
          row.original._isSummary ? (
            <AggregatedCell columnId="pid" aggregated={aggregatedValues} />
          ) : (
            String(cell.getValue() ?? "")
          ),
      },
      {
        id: "profile",
        header: t("ai_profile"),
        accessorKey: "profile",
        enableGrouping: true,
        columnFilterModeOptions: FILTERABLE_COLUMNS,
        Cell: ({ row, cell }) =>
          row.original._isSummary ? (
            <AggregatedCell columnId="profile" aggregated={aggregatedValues} />
          ) : (
            String(cell.getValue() ?? "")
          ),
      },
      {
        id: "count",
        header: t("mttProfileCount"),
        accessorKey: "count",
        enableGrouping: false,
        enableColumnFilter: false,
        Cell: ({ row, cell }) =>
          row.original._isSummary ? (
            <AggregatedCell columnId="count" aggregated={aggregatedValues} />
          ) : (
            formatNumber(cell.getValue())
          ),
      },
      {
        id: "hand_count",
        header: t("mttHandCount"),
        accessorKey: "hand_count",
        enableGrouping: false,
        enableColumnFilter: false,
        Cell: ({ row, cell }) =>
          row.original._isSummary ? (
            <AggregatedCell columnId="hand_count" aggregated={aggregatedValues} />
          ) : (
            formatNumber(cell.getValue())
          ),
      },
      {
        id: "start_time_ms",
        header: t("mttStartTime"),
        accessorKey: "start_time_ms",
        enableGrouping: true,
        columnFilterModeOptions: FILTERABLE_COLUMNS,
        Cell: ({ row, cell }) => {
          if (row.original._isSummary) {
            return <AggregatedCell columnId="start_time_ms" aggregated={aggregatedValues} />;
          }
          const value = cell.getValue<number>();
          return value ? dayjs(value).format("DD.MM.YYYY HH:mm") : "";
        },
      },
      {
        id: "date",
        header: t("date"),
        accessorKey: "date",
        enableGrouping: true,
        columnFilterModeOptions: FILTERABLE_COLUMNS,
        Cell: ({ row, cell }) =>
          row.original._isSummary ? (
            <AggregatedCell columnId="date" aggregated={aggregatedValues} />
          ) : (
            String(cell.getValue() ?? "")
          ),
      },
      {
        id: "buyin_usd",
        header: t("mttBuyinUsd"),
        accessorKey: "buyin_usd",
        enableGrouping: true,
        columnFilterModeOptions: FILTERABLE_COLUMNS,
        Cell: ({ row, cell }) =>
          row.original._isSummary ? (
            <AggregatedCell columnId="buyin_usd" aggregated={aggregatedValues} />
          ) : (
            formatNumber(cell.getValue())
          ),
      },
      {
        id: "tournament",
        header: t("mttTournament"),
        accessorKey: "tournament",
        enableGrouping: true,
        columnFilterModeOptions: FILTERABLE_COLUMNS,
        Cell: ({ row, cell }) =>
          row.original._isSummary ? (
            <AggregatedCell columnId="tournament" aggregated={aggregatedValues} />
          ) : (
            String(cell.getValue() ?? "")
          ),
      },
      {
        id: "game_type",
        header: t("game_type"),
        accessorKey: "game_type",
        enableGrouping: true,
        columnFilterModeOptions: FILTERABLE_COLUMNS,
        Cell: ({ row, cell }) =>
          row.original._isSummary ? (
            <AggregatedCell columnId="game_type" aggregated={aggregatedValues} />
          ) : (
            String(cell.getValue() ?? "")
          ),
      },
      {
        id: "sum_buyin_usd",
        header: t("mttSumBuyinUsd"),
        accessorKey: "sum_buyin_usd",
        enableGrouping: false,
        enableColumnFilter: false,
        Cell: ({ row, cell }) =>
          row.original._isSummary ? (
            <AggregatedCell columnId="sum_buyin_usd" aggregated={aggregatedValues} />
          ) : (
            formatNumber(cell.getValue())
          ),
      },
      {
        id: "avg_buyin_usd",
        header: t("mttAvgBuyinUsd"),
        accessorKey: "avg_buyin_usd",
        enableGrouping: false,
        enableColumnFilter: false,
        Cell: ({ row, cell }) =>
          row.original._isSummary ? (
            <AggregatedCell columnId="avg_buyin_usd" aggregated={aggregatedValues} />
          ) : (
            formatNumber(cell.getValue())
          ),
      },
      {
        id: "sum_rebuy_usd",
        header: t("mttSumRebuyUsd"),
        accessorKey: "sum_rebuy_usd",
        enableGrouping: false,
        enableColumnFilter: false,
        Cell: ({ row, cell }) =>
          row.original._isSummary ? (
            <AggregatedCell columnId="sum_rebuy_usd" aggregated={aggregatedValues} />
          ) : (
            formatNumber(cell.getValue())
          ),
      },
      {
        id: "count_rebuy",
        header: t("mttCountRebuy"),
        accessorKey: "count_rebuy",
        enableGrouping: false,
        enableColumnFilter: false,
        Cell: ({ row, cell }) =>
          row.original._isSummary ? (
            <AggregatedCell columnId="count_rebuy" aggregated={aggregatedValues} />
          ) : (
            formatNumber(cell.getValue())
          ),
      },
      {
        id: "sum_addon_usd",
        header: t("mttSumAddonUsd"),
        accessorKey: "sum_addon_usd",
        enableGrouping: false,
        enableColumnFilter: false,
        Cell: ({ row, cell }) =>
          row.original._isSummary ? (
            <AggregatedCell columnId="sum_addon_usd" aggregated={aggregatedValues} />
          ) : (
            formatNumber(cell.getValue())
          ),
      },
      {
        id: "count_addon",
        header: t("mttCountAddon"),
        accessorKey: "count_addon",
        enableGrouping: false,
        enableColumnFilter: false,
        Cell: ({ row, cell }) =>
          row.original._isSummary ? (
            <AggregatedCell columnId="count_addon" aggregated={aggregatedValues} />
          ) : (
            formatNumber(cell.getValue())
          ),
      },
      {
        id: "reentry",
        header: t("mttReentry"),
        accessorFn: (row) => row.reentry,
        enableGrouping: false,
        enableColumnFilter: false,
        Cell: ({ row, cell }) =>
          row.original._isSummary ? (
            <AggregatedCell columnId="reentry" aggregated={aggregatedValues} />
          ) : (
            formatNumber(cell.getValue())
          ),
      },
      {
        id: "profit_usd",
        header: t("mttProfitUsd"),
        accessorKey: "profit_usd",
        enableGrouping: false,
        enableColumnFilter: false,
        Cell: ({ row, cell }) =>
          row.original._isSummary ? (
            <AggregatedCell columnId="profit_usd" aggregated={aggregatedValues} />
          ) : (
            formatNumber(cell.getValue())
          ),
      },
      {
        id: "room",
        header: t("room"),
        accessorKey: "room",
        enableGrouping: true,
        columnFilterModeOptions: FILTERABLE_COLUMNS,
        Cell: ({ row, cell }) =>
          row.original._isSummary ? (
            <AggregatedCell columnId="room" aggregated={aggregatedValues} />
          ) : (
            String(cell.getValue() ?? "")
          ),
      },
      {
        id: "contributions_usd",
        header: t("mttContributionsUsd"),
        accessorKey: "contributions_usd",
        enableGrouping: false,
        enableColumnFilter: false,
        Cell: ({ row, cell }) =>
          row.original._isSummary ? (
            <AggregatedCell columnId="contributions_usd" aggregated={aggregatedValues} />
          ) : (
            formatNumber(cell.getValue())
          ),
      },
      {
        id: "prize_usd",
        header: t("mttPrizeUsd"),
        accessorKey: "prize_usd",
        enableGrouping: false,
        enableColumnFilter: false,
        Cell: ({ row, cell }) =>
          row.original._isSummary ? (
            <AggregatedCell columnId="prize_usd" aggregated={aggregatedValues} />
          ) : (
            formatNumber(cell.getValue())
          ),
      },
      {
        id: "rake_usd",
        header: t("mttRakeUsd"),
        accessorKey: "rake_usd",
        enableGrouping: false,
        enableColumnFilter: false,
        Cell: ({ row, cell }) =>
          row.original._isSummary ? (
            <AggregatedCell columnId="rake_usd" aggregated={aggregatedValues} />
          ) : (
            formatNumber(cell.getValue())
          ),
      },
      {
        id: "roi_pct",
        header: t("mttRoiPct"),
        accessorKey: "roi_pct",
        enableGrouping: false,
        enableColumnFilter: false,
        Cell: ({ row, cell }) =>
          row.original._isSummary ? (
            <AggregatedCell columnId="roi_pct" aggregated={aggregatedValues} />
          ) : (
            formatPercent(cell.getValue())
          ),
      },
      {
        id: "itm_pct",
        header: t("mttItmPct"),
        accessorKey: "itm_pct",
        enableGrouping: false,
        enableColumnFilter: false,
        Cell: ({ row, cell }) =>
          row.original._isSummary ? (
            <AggregatedCell columnId="itm_pct" aggregated={aggregatedValues} />
          ) : (
            formatPercent(cell.getValue())
          ),
      },
      {
        id: "place",
        header: t("mttPlace"),
        accessorFn: (row) => row.place,
        enableGrouping: false,
        enableColumnFilter: false,
        Cell: ({ row, cell }) =>
          row.original._isSummary ? (
            <AggregatedCell columnId="place" aggregated={aggregatedValues} />
          ) : (
            formatNumber(cell.getValue())
          ),
      },
      {
        id: "avg_num_players",
        header: t("mttAvgNumPlayers"),
        accessorKey: "avg_num_players",
        enableGrouping: false,
        enableColumnFilter: false,
        minSize: 250,
        Cell: ({ row, cell }) =>
          row.original._isSummary ? (
            <AggregatedCell columnId="avg_num_players" aggregated={aggregatedValues} />
          ) : (
            formatNumber(cell.getValue())
          ),
      },
      {
        id: "starting_stack_bb",
        header: t("mttStartingStackBb"),
        accessorFn: (row) => row.starting_stack_bb,
        enableGrouping: false,
        enableColumnFilter: false,
        Cell: ({ row, cell }) =>
          row.original._isSummary ? (
            <AggregatedCell columnId="starting_stack_bb" aggregated={aggregatedValues} />
          ) : (
            formatNumber(cell.getValue())
          ),
      },
    ],
    [aggregatedValues, t]
  );

  const table = useMaterialReactTable({
    data: tableData,
    columns,
    getRowId: (row) =>
      row._isSummary ? SUMMARY_ROW_ID : `${row.pid}-${row.start_time_ms}-${row._rowNumber}`,
    ...defaultProps,
    enableGrouping: true,
    groupedColumnMode: "reorder",
    enableColumnDragging: true,
    enableColumnOrdering: true,
    enableColumnFilterModes: true,
    enableHiding: true,
    manualPagination: true,
    manualFiltering: true,
    manualGrouping: true,
    autoResetPageIndex: true,
    rowCount: response?.data.pagination.items_total,
    state: {
      ...state,
      columnFilterFns,
      isLoading,
      pagination: {
        pageSize: response?.data.pagination.page_size || state.pagination.pageSize,
        pageIndex: response?.data.pagination.page_number || state.pagination.pageIndex,
      },
    },
    muiTableBodyRowProps: ({ row }) => {
      if (row.original._isSummary) {
        return {
          sx: {
            fontWeight: 600,
            backgroundColor: alpha(colorPool[0], 0.35),
          },
        };
      }
      return {};
    },
    muiTablePaperProps: {
      sx: {
        width: "100%",
      },
    },
  });

  return (
    <Grid container paddingY={2} rowSpacing={2} columnSpacing={2}>
      <Box sx={{ display: "flex", minHeight: "400px", width: "100%" }}>
        {isLoading && tableData.length === 0 ? (
          <CircularProgress sx={{ margin: "96px auto 32px" }} />
        ) : (
          <Box sx={{ minHeight: "700px", width: "100%" }}>
            <MaterialReactTable table={table} />
          </Box>
        )}
      </Box>
    </Grid>
  );
};
